"use client";

import { FileText, Headphones, Moon, Sunrise, Wrench } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const steps = [{
  label: "Phase 01",
  title: "Identify repetitive work",
  description: "We review your current processes to identify manual tasks, bottlenecks, duplicated effort, and areas where automation can improve speed, consistency, and control.",
  Icon: FileText,
  elevated: false
}, {
  label: "Phase 02",
  title: "Design and build the solution",
  description: "We create practical automation and technology solutions tailored to your workflow, whether that involves reporting, data transformation, validation checks, or process improvement.",
  Icon: Wrench,
  elevated: true
}, {
  label: "Phase 03",
  title: "Implement and improve",
  description: "We put the solution into use, test it within the workflow, and refine it where needed so the process remains reliable, efficient, and easy to manage.",
  Icon: Sunrise,
  elevated: false
}];
export default function Optimization() {
  const intro = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  return <section data-section-id="7526" id="processes-we-can-improve" className="relative text-foreground py-24 md:py-32 overflow-hidden dark bg-accent">
      {}
      <div aria-hidden="true" className="pointer-events-none absolute -top-32 left-1/2 -translate-x-1/2 w-[520px] h-[520px] rounded-lg blur-3xl opacity-[0.12]" style={{
      background: "hsl(199 89% 48%)"
    }} />

      <div className="relative max-w-[1240px] mx-auto px-6 md:px-12 lg:px-20">
        {}
        <div ref={intro.ref} className={`max-w-[720px] mx-auto text-center mb-16 md:mb-20 transition-all duration-700 ease-out ${intro.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`} style={{
        transitionTimingFunction: "cubic-bezier(0.22, 1, 0.36, 1)"
      }}>
          <h2 className="font-default text-foreground tracking-tight leading-[1.08] !font-medium !w-[780px]" style={{
          fontSize: "clamp(2rem, 4vw, 3.5rem)"
        }}>Three practical steps to&nbsp;<em className="font-default italic font-normal text-secondary" data-ast-id="f:/src/pages/DataAutomationTechnology/sections/Optimization.tsx|sig:3m2zp9" data-ast-start="1955" data-ast-end="2027" contenteditable="false" style={{}}>smarter automation.</em></h2>
          <p className="mt-6 font-default text-muted-foreground leading-relaxed" style={{
          fontSize: "clamp(1.0625rem, 1.2vw, 1.25rem)"
        }}>A structured approach to reduce manual work, improve accuracy, and create more efficient business processes.</p>
        </div>

        {}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {steps.map((step, i) => <StepCard key={i} step={step} index={i} />)}
        </div>
      </div>
    </section>;
}
function StepCard({
  step,
  index
}: {
  step: (typeof steps)[number];
  index: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const {
    Icon
  } = step;
  return <div data-index={index} ref={ref} className={`group relative rounded-lg p-8 bg-card text-card-foreground shadow-md hover:shadow-xl transition-all ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"} ${step.elevated ? "md:-translate-y-2" : ""}`} style={{
    transitionDuration: "720ms",
    transitionDelay: `${index * 120}ms`,
    transitionTimingFunction: "cubic-bezier(0.22, 1, 0.36, 1)"
  }}>
      <div className="inline-flex items-center justify-center w-14 h-14 rounded-lg mb-6" style={{
      backgroundColor: "hsl(199 89% 48% / 0.1)"
    }}>
        <Icon className="w-6 h-6 text-white" strokeWidth={1.75} strokeLinecap="round" strokeLinejoin="round" />
      </div>

      <div className="text-xs font-default font-medium tracking-wider text-muted-foreground mb-3 uppercase">
        {step.label}
      </div>

      <h3 className="font-default font-medium text-foreground tracking-tight leading-tight mb-4" style={{
      fontSize: "clamp(1.375rem, 1.8vw, 1.75rem)"
    }}>
        {step.title}
      </h3>

      <p className="font-default text-base text-muted-foreground leading-relaxed">
        {step.description}
      </p>

      {}
      <div aria-hidden="true" className="absolute inset-0 rounded-lg transition-transform duration-300 ease-out pointer-events-none group-hover:-translate-y-1" />
    </div>;
}